import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  NavigateBefore,
  NavigateNext,
  Save as SaveIcon,
  LocationOn
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UpdateRoommateReq, AddRoommateImg, DeleteRoommateImg, DeleteRoommateReq } from '../../services/advert';
import { GetCountries, GetCities, GetDistrict } from '../../services/location';
import { DEFAULT_IMAGE } from '../../constants';
import GoogleMapReact from 'google-map-react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { GetRoommateById } from '../../services/advert';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import BasicInformationStep from './steps/BasicInformationStep';
import DetailsStep from './steps/DetailsStep';
import ImagesStep from './steps/ImagesStep';

const Marker = ({ text }) => (
  <LocationOn 
    sx={{ 
      color: 'error.main',
      transform: 'translate(-50%, -100%)'
    }} 
  />
);

const EditRoommate = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [mapCenter, setMapCenter] = useState({
    lat: 41.0082,
    lng: 28.9784
  });

  const [formData, setFormData] = useState({
    id: '',
    listingTitle: '',
    country: '',
    city: '',
    district: '',
    address: '',
    description: '',
    monthlyRent: '',
    utilitiesIncluded: false,
    amenities: '',
    availableFrom: new Date(),
    gender: '',
    preferredAgeRangeStart: '',
    preferredAgeRangeEnd: '',
    petsAllowed: false,
    smokingAllowed: false,
    alcoholAllowed: false,
    latitude: '',
    longitude: ''
  });

  const [locations, setLocations] = useState({
    countries: [],
    cities: [],
    districts: []
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchCountries();
    if (id) {
      fetchRoommateDetails();
    }
  }, [id]);

  useEffect(() => {
    if (formData.country) {
      fetchCities(formData.country);
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.city) {
      fetchDistricts(formData.city);
    }
  }, [formData.city]);

  const fetchRoommateDetails = async () => {
    try {
      const response = await GetRoommateById(id);
      if (response.statusCode === 200) {
        const data = response.data;
        setFormData({
          id: data.id,
          listingTitle: data.listingTitle,
          country: data.country,
          city: data.city,
          district: data.district,
          address: data.address,
          description: data.description,
          monthlyRent: data.monthlyRent,
          utilitiesIncluded: data.utilitiesIncluded,
          amenities: data.amenities,
          availableFrom: new Date(data.availableFrom),
          gender: data.preferredGender,
          preferredAgeRangeStart: data.preferredAgeRangeStart,
          preferredAgeRangeEnd: data.preferredAgeRangeEnd,
          petsAllowed: data.petsAllowed,
          smokingAllowed: data.smokingAllowed,
          alcoholAllowed: data.alcoholAllowed,
          latitude: data.latitude,
          longitude: data.longitude
        });
        
        setImages(data.images || []);
        if (data.latitude && data.longitude) {
          setMapCenter({
            lat: parseFloat(data.latitude),
            lng: parseFloat(data.longitude)
          });
        }
      }
    } catch (error) {
      setError(t('common.error'));
      toast.error(t('common.error'));
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await GetCountries();
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          countries: response.data
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const fetchCities = async (countryId) => {
    try {
      const response = await GetCities(countryId);
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          cities: response.data,
          districts: []
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await GetDistrict(cityId);
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          districts: response.data
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleCountryChange = async (event) => {
    const countryId = event.target.value;
    setFormData(prev => ({ ...prev, country: countryId, city: '', district: '' }));
  };

  const handleCityChange = async (event) => {
    const cityId = event.target.value;
    setFormData(prev => ({ ...prev, city: cityId, district: '' }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("RID", id);
    formData.append("FileName", file);

    try {
      const response = await AddRoommateImg(formData);
      if (response.statusCode === 200) {
        setImages(prev => [...prev, {
          id: response.data,
          url: URL.createObjectURL(file),
          type: 2
        }]);
        toast.success(t('common.imageUploaded'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleImageDelete = async (imageId) => {
    try {
      const response = await DeleteRoommateImg(imageId);
      if (response.statusCode === 200) {
        setImages(prev => prev.filter(img => img.id !== imageId));
        toast.success(t('common.imageDeleted'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const requestData = {
        ...formData,
        latitute: formData.latitude,
        longtitute: formData.longitude
      };
      const response = await UpdateRoommateReq(requestData);
      if (response.statusCode === 200) {
        toast.success(t('common.updateSuccess'));
        navigate('/my-advertisements');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const deleteRoommate = async () => {
    try {
      const response = await DeleteRoommateReq(id);
      if (response.statusCode === 200) {
        toast.success(t('editRoommate.deleteSuccess'));
        navigate('/my-advertisements');
      } else {
        toast.error(t('editRoommate.deleteError'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
    setDeleteDialogOpen(false);
  };

  const steps = [
    t('editRoommate.steps.basic'),
    t('editRoommate.steps.details'),
    t('editRoommate.steps.images')
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('editRoommate.title')}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step content */}
        {activeStep === 0 && (
          <BasicInformationStep
            formData={formData}
            setFormData={setFormData}
            locations={locations}
            handleCountryChange={handleCountryChange}
            handleCityChange={handleCityChange}
            mapCenter={mapCenter}
            setMapCenter={setMapCenter}
            t={t}
          />
        )}
        {activeStep === 1 && (
          <DetailsStep
            formData={formData}
            setFormData={setFormData}
            t={t}
          />
        )}
        {activeStep === 2 && (
          <ImagesStep
            handleImageUpload={handleImageUpload}
            images={images}
            t={t}
          />
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            onClick={() => setActiveStep((prev) => prev - 1)}
            disabled={activeStep === 0}
            startIcon={<NavigateBefore />}
          >
            {t('common.back')}
          </Button>

          <Box>
            <Button
              color="error"
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              sx={{ mr: 1 }}
            >
              {t('common.delete.index')}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
              >
                {t('common.save')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => setActiveStep((prev) => prev + 1)}
                endIcon={<NavigateNext />}
              >
                {t('common.next')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={deleteRoommate}
      />
    </Container>
  );
};

export default EditRoommate; 